/**
 * =============================================================================
 * SearchHighlightBar.tsx — Pillola flottante di navigazione tra le occorrenze
 * della ricerca globale evidenziate in pagina.
 * =============================================================================
 *
 * Si attiva quando l'URL contiene `?q=`. Trova tutti gli elementi
 * `mark.search-hit` presenti nel `<main>` corrente, marca l'occorrenza attiva
 * con `.search-hit-active`, scrolla per portarla nel viewport e mostra una
 * pillola "Occorrenza X di Y" con pulsanti Prec./Succ./Chiudi.
 *
 * Scorciatoie:
 *  - Enter / F3        → occorrenza successiva
 *  - Shift+Enter / Shift+F3 → occorrenza precedente
 *  - Esc               → chiude la barra e rimuove le evidenziazioni
 *
 * Le opzioni di ricerca (case, parola, regex) viaggiano negli stessi query
 * params usati da {@link ProcessedContent}, così la sintassi resta
 * condivisa fra ricerca globale, evidenziazione in pagina e navigazione.
 * =============================================================================
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronUp, ChevronDown, X, Search as SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { clearHighlights } from "@/lib/searchHighlight";

const ACTIVE_CLASS = "search-hit-active";

export function SearchHighlightBar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const term = (searchParams.get("q") || "").trim();
  const caseFlag = searchParams.get("case") === "1";
  const wordFlag = searchParams.get("word") === "1";
  const regexFlag = searchParams.get("regex") === "1";

  // Lista dei marker correnti — ri-calcolata reattivamente quando il DOM cambia
  // (es. il contenuto viene caricato in più passi o il termine cambia).
  const [marks, setMarks] = useState<HTMLElement[]>([]);
  const [index, setIndex] = useState(0);

  // Ricostruisce la lista dei marker visibili nella main area.
  const collectMarks = useCallback(() => {
    if (!term) {
      setMarks([]);
      return;
    }
    const scope =
      (document.querySelector('[data-tour-id="main-content"]') as HTMLElement | null) ||
      document.body;
    const list = Array.from(
      scope.querySelectorAll<HTMLElement>("mark.search-hit"),
    );
    setMarks(list);
    setIndex(0);
  }, [term]);

  // MutationObserver per restare in sincronia con il rendering progressivo.
  useEffect(() => {
    if (!term) return;
    let raf = 0;
    const trigger = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(collectMarks);
    };
    trigger();
    const target =
      (document.querySelector('[data-tour-id="main-content"]') as HTMLElement | null) ||
      document.body;
    const obs = new MutationObserver(trigger);
    obs.observe(target, { childList: true, subtree: true, characterData: true });
    return () => {
      obs.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [term, caseFlag, wordFlag, regexFlag, collectMarks]);

  // Applica/rimuove la classe attiva e scrolla nel viewport.
  useEffect(() => {
    if (marks.length === 0) return;
    marks.forEach((m) => m.classList.remove(ACTIVE_CLASS));
    const active = marks[Math.min(index, marks.length - 1)];
    if (active) {
      active.classList.add(ACTIVE_CLASS);
      active.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [marks, index]);

  const go = useCallback(
    (delta: number) => {
      setMarks((current) => {
        if (current.length === 0) return current;
        setIndex((i) => (i + delta + current.length) % current.length);
        return current;
      });
    },
    [],
  );

  const close = useCallback(() => {
    clearHighlights();
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("case");
    next.delete("word");
    next.delete("regex");
    setSearchParams(next, { replace: true });
    setMarks([]);
  }, [searchParams, setSearchParams]);

  // Scorciatoie da tastiera globali — disabilitate se l'utente sta scrivendo
  // in un campo testuale, così non interferiamo con form e editor.
  useEffect(() => {
    if (!term) return;
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      const editing =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (t && (t as HTMLElement).isContentEditable);
      if (editing) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "Enter" || e.key === "F3") {
        e.preventDefault();
        go(e.shiftKey ? -1 : 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [term, go, close]);

  // Quando il termine sparisce dall'URL, pulisce eventuali evidenziazioni.
  useEffect(() => {
    if (!term) clearHighlights();
  }, [term]);

  const label = useMemo(() => {
    if (!term) return "";
    if (marks.length === 0) return "Nessuna occorrenza";
    return `Occorrenza ${index + 1} di ${marks.length}`;
  }, [term, marks.length, index]);

  if (!term) return null;

  return (
    <div
      role="region"
      aria-label="Navigazione risultati ricerca in pagina"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full border bg-background/95 backdrop-blur px-3 py-1.5 shadow-lg"
    >
      <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="text-xs font-medium truncate max-w-[24ch]" title={term}>
        “{term}”
      </span>
      <Badge variant="secondary" className="text-xs">
        {label}
      </Badge>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={() => go(-1)}
        disabled={marks.length === 0}
        aria-label="Occorrenza precedente"
        title="Precedente (Shift+Enter)"
      >
        <ChevronUp className="w-4 h-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={() => go(1)}
        disabled={marks.length === 0}
        aria-label="Occorrenza successiva"
        title="Successiva (Enter)"
      >
        <ChevronDown className="w-4 h-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        onClick={close}
        aria-label="Chiudi navigazione ricerca"
        title="Chiudi (Esc)"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}
